/**
 * JiraForm Component
 *
 * Inline form for creating Jira tickets from comments.
 * Implements Treatment A: Inline Expansion pattern.
 *
 * Features:
 * - Manual-first approach with optional AI generation
 * - Animated expand/collapse transitions
 * - All form states: edit, loading, submitting, success, error
 * - Accessible with keyboard navigation and screen reader support
 * - Fixed height with internal scroll for content area
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type {
  JiraFormState,
  JiraFormFields,
  JiraProject,
  JiraIssueType,
  JiraTicket,
  JiraError,
} from '../../lib/types';
import { tokens } from '../../styles/tokens';
import {
  SpinnerIcon,
  SparkleIcon,
  ExternalLinkIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '../icons/Icons';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ShimmerSelect } from '../shared/Shimmer';

export interface JiraFormProps {
  /** Current form state */
  formState: JiraFormState;
  /** Form field values */
  fields: JiraFormFields;
  /** Available projects */
  projects: JiraProject[];
  /** Available issue types */
  issueTypes: JiraIssueType[];
  /** Whether AI generation is in progress */
  isManualMode: boolean;
  /** Created ticket info (when in success state) */
  createdTicket?: JiraTicket;
  /** Error info (when in error state) */
  error?: JiraError;
  /** Whether metadata is loading */
  isLoadingMetadata?: boolean;
  /** Callback when fields change */
  onFieldsChange: (fields: JiraFormFields) => void;
  /** Callback to generate with AI */
  onGenerateWithAI: () => void;
  /** Callback to submit the form */
  onSubmit: () => void;
  /** Callback to cancel/close the form */
  onCancel: () => void;
  /** Callback to retry after error */
  onRetry: () => void;
  /** Callback to open ticket in Jira */
  onOpenInJira?: (ticket: JiraTicket) => void;
}

const FORM_HEIGHT = 450;
const MAX_SUMMARY_LENGTH = 100;
const SUMMARY_WARNING_THRESHOLD = 90;

export const JiraForm: React.FC<JiraFormProps> = ({
  formState,
  fields,
  projects,
  issueTypes,
  isManualMode,
  createdTicket,
  error,
  isLoadingMetadata = false,
  onFieldsChange,
  onGenerateWithAI,
  onSubmit,
  onCancel,
  onRetry,
  onOpenInJira,
}) => {
  const summaryRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [announcement, setAnnouncement] = useState<string>('');
  const isMobile = useIsMobile();

  // Focus summary field when form opens or AI generation completes
  useEffect(() => {
    if (formState === 'edit' && summaryRef.current) {
      summaryRef.current.focus();
    }
  }, [formState]);

  // Scroll form into view when it opens
  useEffect(() => {
    if (formState !== 'default' && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [formState]);

  // Screen reader announcements
  useEffect(() => {
    switch (formState) {
      case 'edit':
        setAnnouncement(
          isManualMode
            ? 'Jira ticket form opened. Summary field focused.'
            : 'Ticket content generated. Review and edit as needed.'
        );
        break;
      case 'loading':
        setAnnouncement('Generating ticket content. Please wait.');
        break;
      case 'submitting':
        setAnnouncement('Creating Jira ticket. Please wait.');
        break;
      case 'success':
        setAnnouncement(`Ticket ${createdTicket?.key} created successfully.`);
        break;
      case 'error':
        setAnnouncement(`Failed to create ticket. ${error?.message || 'Something went wrong.'}`);
        break;
    }
  }, [formState, isManualMode, createdTicket?.key, error?.message]);

  const handleFieldChange = useCallback(
    (field: keyof JiraFormFields, value: string) => {
      onFieldsChange({ ...fields, [field]: value });
    },
    [fields, onFieldsChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Cmd/Ctrl+Enter submits
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && formState === 'edit') {
        e.preventDefault();
        onSubmit();
      }

      // Focus trap: Tab on last element wraps to first, Shift+Tab on first wraps to last
      if (e.key === 'Tab') {
        const focusableElements = formRef.current?.querySelectorAll(
          'button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled)'
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [formState, onSubmit]
  );

  // Handle Enter in summary field to move focus to description
  const handleSummaryKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        descriptionRef.current?.focus();
      }
    },
    []
  );

  const handleOpenInJira = useCallback(() => {
    if (createdTicket && onOpenInJira) {
      onOpenInJira(createdTicket);
    } else if (createdTicket) {
      window.open(createdTicket.url, '_blank', 'noopener,noreferrer');
    }
  }, [createdTicket, onOpenInJira]);

  const summaryLength = fields.summary.length;
  const isSummaryWarning = summaryLength >= SUMMARY_WARNING_THRESHOLD;
  const isFieldsEmpty = !fields.summary.trim() && !fields.description.trim();
  const isSubmitting = formState === 'submitting';
  const canSubmit = formState === 'edit' || isSubmitting;

  // Derive effective issue type - prefer Task, fall back to first
  const getEffectiveIssueTypeId = () => {
    if (fields.issueTypeId && issueTypes.some(t => t.id === fields.issueTypeId)) {
      return fields.issueTypeId;
    }
    const taskType = issueTypes.find(t => t.name.toLowerCase() === 'task');
    return taskType?.id || issueTypes[0]?.id || '';
  };
  const effectiveIssueTypeId = getEffectiveIssueTypeId();

  // Render loading state
  if (formState === 'loading') {
    return (
      <div
        ref={formRef}
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={formContainerStyle}
        data-testid="jira-form-loading"
      >
        <div style={centeredContentStyle}>
          <SpinnerIcon size={32} color="#4f32ce" />
          <h3 style={loadingHeadingStyle}>Generating Ticket...</h3>
          <p style={loadingSubtextStyle}>Analyzing the comment thread and context</p>
        </div>
        <div aria-live="polite" className="sr-only">
          {announcement}
        </div>
      </div>
    );
  }

  // Render success state
  if (formState === 'success' && createdTicket) {
    return (
      <div
        ref={formRef}
        role="status"
        aria-live="polite"
        style={{ ...formContainerStyle, ...successContainerStyle }}
        data-testid="jira-form-success"
      >
        <div style={centeredContentStyle}>
          <div style={successIconContainerStyle}>
            <CheckCircleIcon size={32} color="#217a37" />
          </div>
          <h3 style={successHeadingStyle}>Ticket Created!</h3>
          <p style={successSubtextStyle}>
            Issue {createdTicket.key} has been added to the backlog
          </p>
          <div style={successActionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={secondaryButtonStyle}
              data-testid="jira-form-close"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleOpenInJira}
              style={linkButtonStyle}
              data-testid="jira-form-open-in-jira"
            >
              Open in Jira
              <ExternalLinkIcon size={14} color="#4f32ce" />
            </button>
          </div>
        </div>
        <div aria-live="polite" className="sr-only">
          {announcement}
        </div>
      </div>
    );
  }

  // Render error state
  if (formState === 'error') {
    return (
      <div
        ref={formRef}
        role="alert"
        aria-live="assertive"
        style={{ ...formContainerStyle, ...errorContainerStyle }}
        data-testid="jira-form-error"
      >
        <div style={centeredContentStyle}>
          <div style={errorIconContainerStyle}>
            <AlertTriangleIcon size={32} color="#bc1c11" />
          </div>
          <h3 style={errorHeadingStyle}>Failed to create ticket</h3>
          <p style={errorSubtextStyle}>
            {error?.message || 'Something went wrong. Please try again.'}
          </p>
          <div style={errorActionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={secondaryButtonStyle}
              data-testid="jira-form-cancel-error"
            >
              Cancel
            </button>
            {error?.isRetryable !== false && (
              <button
                type="button"
                onClick={onRetry}
                style={retryButtonStyle}
                data-testid="jira-form-retry"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
        <div aria-live="assertive" className="sr-only">
          {announcement}
        </div>
      </div>
    );
  }

  // Render edit state (main form)
  return (
    <div
      ref={formRef}
      role="dialog"
      aria-label="Create Jira ticket"
      aria-modal="false"
      style={{
        ...formContainerStyle,
        ...(isMobile ? mobileFormContainerStyle : {}),
      }}
      onKeyDown={handleKeyDown}
      data-testid="jira-form"
      data-mobile={isMobile ? 'true' : 'false'}
    >
      {/* Form Header */}
      <div style={formHeaderStyle}>
        <span style={headerDotStyle} />
        <span style={headerTitleStyle}>New Jira Ticket</span>
      </div>

      {/* Scrollable Content Area */}
      <div style={{
        ...formContentStyle,
        ...(isMobile ? mobileFormContentStyle : {}),
      }}>
        {/* Project and Issue Type Selectors */}
        <div
          style={{
            ...dropdownRowStyle,
            ...(isMobile ? mobileDropdownRowStyle : {}),
          }}
          data-testid="jira-dropdown-row"
        >
          <div style={dropdownContainerStyle}>
            <label htmlFor="jira-project" style={labelStyle}>
              Project
            </label>
            {isLoadingMetadata ? (
              <ShimmerSelect data-testid="jira-project-shimmer" />
            ) : (
              <div style={selectWrapperStyle}>
                <select
                  id="jira-project"
                  value={fields.projectKey}
                  onChange={(e) => handleFieldChange('projectKey', e.target.value)}
                  disabled={formState === 'submitting'}
                  style={selectStyle}
                  data-testid="jira-project-select"
                >
                  {projects.map((project) => (
                    <option key={project.key} value={project.key}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={14} color="#6a6e7a" />
              </div>
            )}
          </div>

          <div style={dropdownContainerStyle}>
            <label htmlFor="jira-issue-type" style={labelStyle}>
              Issue Type
            </label>
            {isLoadingMetadata ? (
              <ShimmerSelect data-testid="jira-issue-type-shimmer" />
            ) : (
              <div style={selectWrapperStyle}>
                <select
                  id="jira-issue-type"
                  value={effectiveIssueTypeId}
                  onChange={(e) => handleFieldChange('issueTypeId', e.target.value)}
                  disabled={formState === 'submitting'}
                  style={selectStyle}
                  data-testid="jira-issue-type-select"
                >
                  {issueTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={14} color="#6a6e7a" />
              </div>
            )}
          </div>
        </div>

        {/* Generate with AI Button - only show when fields are empty and in manual mode */}
        {isManualMode && isFieldsEmpty && (
          <button
            type="button"
            onClick={onGenerateWithAI}
            style={aiButtonStyle}
            data-testid="jira-generate-ai"
          >
            <SparkleIcon size={16} color="#ffffff" />
            Generate with AI
          </button>
        )}

        {/* Summary Field */}
        <div style={fieldContainerStyle}>
          <div style={labelRowStyle}>
            <label htmlFor="jira-summary" style={labelStyle}>
              Summary
            </label>
            <span
              style={{
                ...charCountStyle,
                color: isSummaryWarning ? '#b06300' : '#6a6e7a',
              }}
              aria-live="polite"
              aria-atomic="true"
            >
              {summaryLength}/{MAX_SUMMARY_LENGTH}
            </span>
          </div>
          <input
            ref={summaryRef}
            id="jira-summary"
            type="text"
            value={fields.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value.slice(0, MAX_SUMMARY_LENGTH))}
            onKeyDown={handleSummaryKeyDown}
            placeholder={
              isManualMode
                ? 'e.g., Mobile button styling issues on /pricing page'
                : 'Brief summary of the issue'
            }
            disabled={formState === 'submitting'}
            style={inputStyle}
            maxLength={MAX_SUMMARY_LENGTH}
            data-testid="jira-summary-input"
          />
        </div>

        {/* Description Field */}
        <div style={fieldContainerStyle}>
          <label htmlFor="jira-description" style={labelStyle}>
            Description
          </label>
          <textarea
            ref={descriptionRef}
            id="jira-description"
            value={fields.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder={
              isManualMode
                ? 'Describe the issue, steps to reproduce, and expected behavior...\n\nTip: Include reproduction steps and acceptance criteria.'
                : 'Detailed description...'
            }
            disabled={formState === 'submitting'}
            style={textareaStyle}
            data-testid="jira-description-input"
          />
        </div>
      </div>

      {/* Fixed Footer with Actions */}
      <div style={{
        ...formFooterStyle,
        ...(isMobile ? mobileFormFooterStyle : {}),
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={formState === 'submitting'}
          style={{
            ...cancelButtonStyle,
            ...(isMobile ? mobileButtonStyle : {}),
          }}
          data-testid="jira-cancel-button"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
          style={{
            ...primaryButtonStyle,
            ...(isMobile ? mobileButtonStyle : {}),
            opacity: isSubmitting ? 0.7 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
          }}
          aria-describedby="submit-hint"
          data-testid="jira-submit-button"
        >
          {isSubmitting ? (
            <>
              <SpinnerIcon size={14} color="#ffffff" />
              Creating...
            </>
          ) : (
            'Create Ticket'
          )}
        </button>
        <span id="submit-hint" className="sr-only">
          Press Cmd+Enter to submit
        </span>
      </div>

      {/* Screen reader announcements */}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
};

// ============================================================================
// Styles using PDS Tokens
// ============================================================================

const formContainerStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.bgSecondary,
  border: `${tokens.borders.widthDefault} solid ${tokens.colors.primaryBorder}`,
  borderRadius: tokens.borders.radiusContainer,
  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
  height: `${FORM_HEIGHT}px`,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  marginTop: tokens.spacing.m,
};

const successContainerStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.successBg,
  borderColor: tokens.colors.success,
};

const errorContainerStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.criticalBg,
  borderColor: tokens.colors.critical,
};

const centeredContentStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: tokens.spacing.xl,
  textAlign: 'center',
  gap: tokens.spacing.s,
};

const formHeaderStyle: React.CSSProperties = {
  padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing['2xs'],
  borderBottom: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
};

const headerDotStyle: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: tokens.colors.primary,
};

const headerTitleStyle: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.textDefault,
};

const formContentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: tokens.spacing.m,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing.m,
};

const formFooterStyle: React.CSSProperties = {
  padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
  borderTop: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: tokens.colors.bgSecondary,
};

const dropdownRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: tokens.spacing.s,
};

const dropdownContainerStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing['5xs'],
};

const labelStyle: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.xs,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.textSecondary,
};

const labelRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const charCountStyle: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.xs,
  fontWeight: tokens.typography.fontWeight.regular,
};

const selectWrapperStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: `${tokens.spacing['2xs']} ${tokens.spacing.xl} ${tokens.spacing['2xs']} ${tokens.spacing.s}`,
  fontSize: tokens.typography.fontSize.s,
  color: tokens.colors.textDefault,
  backgroundColor: tokens.colors.bgDefault,
  border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderInput}`,
  borderRadius: tokens.borders.radiusInput,
  appearance: 'none',
  cursor: 'pointer',
};

const fieldContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing['5xs'],
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: `${tokens.spacing.xs} ${tokens.spacing.s}`,
  fontSize: tokens.typography.fontSize.s,
  color: tokens.colors.textDefault,
  backgroundColor: tokens.colors.bgDefault,
  border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderInput}`,
  borderRadius: tokens.borders.radiusInput,
  outline: 'none',
  boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '120px',
  padding: `${tokens.spacing.xs} ${tokens.spacing.s}`,
  fontSize: tokens.typography.fontSize.s,
  fontFamily: tokens.typography.fontFamilyMono,
  color: tokens.colors.textDefault,
  backgroundColor: tokens.colors.bgDefault,
  border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderInput}`,
  borderRadius: tokens.borders.radiusInput,
  outline: 'none',
  resize: 'none',
  boxSizing: 'border-box',
};

const aiButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.textReverse,
  background: `linear-gradient(135deg, #6366f1 0%, ${tokens.colors.primary} 100%)`,
  border: 'none',
  borderRadius: tokens.borders.radiusContainer,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: tokens.spacing['2xs'],
};

const cancelButtonStyle: React.CSSProperties = {
  padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.textSecondary,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: tokens.borders.radiusButton,
  cursor: 'pointer',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.buttonPrimaryFg,
  backgroundColor: tokens.colors.buttonPrimaryBg,
  border: 'none',
  borderRadius: tokens.borders.radiusButton,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing['3xs'],
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.textSecondary,
  backgroundColor: tokens.colors.bgDefault,
  border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderInput}`,
  borderRadius: tokens.borders.radiusButton,
  cursor: 'pointer',
};

const linkButtonStyle: React.CSSProperties = {
  padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.primary,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: tokens.borders.radiusButton,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing['3xs'],
};

const retryButtonStyle: React.CSSProperties = {
  padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
  fontSize: tokens.typography.fontSize.s,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.criticalFg,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: tokens.borders.radiusButton,
  cursor: 'pointer',
};

const loadingHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.m,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.textDefault,
};

const loadingSubtextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.s,
  color: tokens.colors.textSecondary,
};

const successIconContainerStyle: React.CSSProperties = {
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: tokens.colors.success,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const successHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.l,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.successFg,
};

const successSubtextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.s,
  color: tokens.colors.textDefault,
};

const successActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: tokens.spacing.s,
  marginTop: tokens.spacing['2xs'],
};

const errorIconContainerStyle: React.CSSProperties = {
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: tokens.colors.critical,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const errorHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.l,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.colors.criticalFg,
};

const errorSubtextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.s,
  color: tokens.colors.textDefault,
};

const errorActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: tokens.spacing.s,
  marginTop: tokens.spacing['2xs'],
};

// ============================================================================
// Mobile-Specific Styles (Task 7.4.13)
// Applied when viewport <= 480px
// ============================================================================

const mobileFormContainerStyle: React.CSSProperties = {
  // Full available width on mobile
  width: '100%',
};

const mobileFormContentStyle: React.CSSProperties = {
  // Reduced padding on mobile (12px instead of 16px)
  padding: '12px',
};

const mobileDropdownRowStyle: React.CSSProperties = {
  // Stack dropdowns vertically on mobile
  flexDirection: 'column',
};

const mobileFormFooterStyle: React.CSSProperties = {
  // Stack buttons vertically on mobile
  flexDirection: 'column',
  gap: tokens.spacing.s,
  padding: '12px',
};

const mobileButtonStyle: React.CSSProperties = {
  // Full width buttons on mobile with minimum 44px tap target
  width: '100%',
  minHeight: '44px',
  padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
};

export default JiraForm;
